interface BrandRingProps {
  /** outer diameter in px */
  size?: number;
  label?: string;
}

/**
 * Prominent branded loading mark — a smooth conic "comet" ring with an
 * orbiting spark, expanding ripples and a pulsing gold core.
 * Shared by the page / table / overlay loaders.
 */
export const BrandRing: React.FC<BrandRingProps> = ({
  size = 92,
  label = "S",
}) => {
  const ringWidth = Math.max(4, Math.round(size * 0.055));
  const core = Math.round(size * 0.36);

  return (
    <div
      className="relative flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      {/* expanding ripple rings */}
      {[0, 1].map((i) => (
        <span
          key={i}
          className="absolute inset-0 rounded-full border border-[#BBA473]/40"
          style={{
            animation: "admin-ripple 2.4s ease-out infinite",
            animationDelay: `${i * 1.2}s`,
          }}
        />
      ))}

      {/* soft glow */}
      <span
        className="absolute inset-[8%] rounded-full blur-2xl"
        style={{
          background:
            "radial-gradient(circle, rgba(187,164,115,0.5) 0%, rgba(187,164,115,0) 70%)",
          animation: "admin-pulse-core 2s ease-in-out infinite",
        }}
      />

      {/* static track */}
      <span
        className="absolute inset-0 rounded-full"
        style={{ border: `${ringWidth}px solid rgba(187,164,115,0.12)` }}
      />

      {/* conic comet ring */}
      <span
        className="absolute inset-0 rounded-full"
        style={{
          background:
            "conic-gradient(from 90deg, rgba(187,164,115,0) 0deg, rgba(187,164,115,0.15) 90deg, #BBA473 300deg, #E8D5A3 360deg)",
          WebkitMask: `radial-gradient(farthest-side, transparent calc(100% - ${ringWidth}px), #000 calc(100% - ${ringWidth}px))`,
          mask: `radial-gradient(farthest-side, transparent calc(100% - ${ringWidth}px), #000 calc(100% - ${ringWidth}px))`,
          animation:
            "admin-spin 1.1s cubic-bezier(0.5, 0.15, 0.5, 0.85) infinite",
        }}
      />

      {/* orbiting spark */}
      <span
        className="absolute inset-0"
        style={{ animation: "admin-orbit 1.6s linear infinite" }}
      >
        <span
          className="absolute left-1/2 top-0 -translate-x-1/2 rounded-full bg-[#F3E4BC]"
          style={{
            width: ringWidth + 2,
            height: ringWidth + 2,
            boxShadow: "0 0 12px 2px rgba(232,213,163,0.8)",
          }}
        />
      </span>

      {/* pulsing brand core */}
      <span
        className="relative flex items-center justify-center rounded-xl bg-gradient-to-br from-[#D8BE86] to-[#B9A06A] font-bold text-[#211a0d]"
        style={{
          width: core,
          height: core,
          fontSize: core * 0.42,
          animation: "admin-pulse-core 2s ease-in-out infinite",
        }}
      >
        {label}
      </span>
    </div>
  );
};

/** Three bouncing gold dots — pairs with <BrandRing />. */
export const BrandDots = () => (
  <div className="flex gap-2">
    {[0, 150, 300].map((delay) => (
      <span
        key={delay}
        className="w-2 h-2 bg-[#BBA473] rounded-full animate-bounce"
        style={{ animationDelay: `${delay}ms` }}
      />
    ))}
  </div>
);
